/**
 * Thin wrapper around SortableJS for Vue 3 template refs.
 *   const { attach } = useSortable({ handle: ".drag-handle", onEnd: (evt) => { revertDom(evt); moveItem(list, evt.oldIndex, evt.newIndex) } })
 *   onMounted(() => attach(listEl.value));
 * SortableJS moves the DOM node itself; call `revertDom(evt)` first so Vue's keyed
 * re-render (driven by the array mutation) is the only thing that reorders the DOM.
 */
import Sortable from "sortablejs";
import { onBeforeUnmount } from "vue";

export function useSortable(options = {}) {
  const instances = [];
  const attach = (el, override = {}) => {
    if (!el) return null;
    const s = Sortable.create(el, {
      animation: 150,
      ghostClass: "sortable-ghost",
      chosenClass: "sortable-chosen",
      dragClass: "sortable-drag",
      // mouse/touch fallback instead of native HTML5 drag: identical behaviour on desktop,
      // tablets and Safari, and the dragged clone is not clipped by scrolling containers
      forceFallback: true,
      fallbackOnBody: true,
      fallbackTolerance: 3,
      ...options,
      ...override,
    });
    instances.push(s);
    return s;
  };
  const destroyAll = () => {
    while (instances.length) {
      const s = instances.pop();
      try { s.destroy(); } catch (e) { /* element already gone */ }
    }
  };
  onBeforeUnmount(destroyAll);
  return { attach, destroyAll };
}

/** put the dragged node back where SortableJS took it from (Vue will re-render the new order) */
export function revertDom(evt) {
  const { item, from, to, oldIndex } = evt || {};
  if (!item || !from) return;
  try {
    if (to && to !== from) to.removeChild(item);
    else if (item.parentNode) item.parentNode.removeChild(item);
    const ref = from.children[oldIndex] || null;
    from.insertBefore(item, ref);
  } catch (e) { /* DOM already reconciled */ }
}

/** move item inside an array (mutating), used after a sortable `onEnd` */
export function moveItem(arr, from, to) {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const [item] = arr.splice(from, 1);
  arr.splice(to, 0, item);
  return arr;
}

/** move an item between two arrays (mutating), used for cross-list drags */
export function transferItem(fromArr, toArr, fromIndex, toIndex) {
  if (fromIndex < 0 || fromIndex >= fromArr.length) return;
  const [item] = fromArr.splice(fromIndex, 1);
  toArr.splice(Math.min(Math.max(toIndex, 0), toArr.length), 0, item);
}
